import React, { useState, useEffect, useCallback } from 'react';
import { collection, query, where, getDocs, addDoc, deleteDoc, doc, serverTimestamp } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { db, storage } from '../firebase';
import './TeacherLearningMaterialsView.css';

const MATERIAL_TYPES = ['PDF', 'Document', 'Image', 'Video', 'Link', 'Presentation'];

function getTypeIcon(type) {
  const icons = {
    PDF: '📄',
    Document: '📃',
    Image: '🖼️',
    Video: '🎬',
    Link: '🔗',
    Presentation: '📊'
  };
  return icons[type] || '📄';
}

function TeacherLearningMaterialsView({ teacherData }) {
  const [classes, setClasses] = useState([]);
  const [selectedClassId, setSelectedClassId] = useState('');
  const [materials, setMaterials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMaterials, setLoadingMaterials] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    type: 'PDF',
    url: '',
    file: null
  });

  const loadClasses = useCallback(async () => {
    if (!teacherData?.uid) {
      setLoading(false);
      return;
    }
    try {
      const classesQuery = query(
        collection(db, 'classes'),
        where('teacherId', '==', teacherData.uid)
      );
      const snapshot = await getDocs(classesQuery);
      let classesData = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));

      // Also load classes where subjectTeacherAssignments contains the teacher's subject
      if (teacherData.specialtySubject) {
        const allClassesQuery = query(collection(db, 'classes'));
        const allSnapshot = await getDocs(allClassesQuery);
        const allClasses = allSnapshot.docs.map(d => ({ id: d.id, ...d.data() }));
        const subjectClasses = allClasses.filter(c =>
          (c.subjectTeacherAssignments || []).includes(teacherData.specialtySubject) &&
          !classesData.find(ec => ec.id === c.id)
        );
        classesData = [...classesData, ...subjectClasses];
      }

      setClasses(classesData);
      if (classesData.length > 0) {
        setSelectedClassId(classesData[0].id);
      }
    } catch (error) {
      console.error('Error loading classes:', error);
    } finally {
      setLoading(false);
    }
  }, [teacherData]);

  useEffect(() => {
    loadClasses();
  }, [loadClasses]);

  const loadMaterials = useCallback(async (classId) => {
    if (!classId) {
      setMaterials([]);
      return;
    }
    setLoadingMaterials(true);
    try {
      const materialsRef = collection(db, 'classes', classId, 'learningMaterials');
      const snapshot = await getDocs(materialsRef);
      const materialsData = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
      materialsData.sort((a, b) => {
        const aTime = a.uploadedAt?.toDate ? a.uploadedAt.toDate().getTime() : 0;
        const bTime = b.uploadedAt?.toDate ? b.uploadedAt.toDate().getTime() : 0;
        return bTime - aTime;
      });
      setMaterials(materialsData);
    } catch (error) {
      console.error('Error loading materials:', error);
      setMaterials([]);
    } finally {
      setLoadingMaterials(false);
    }
  }, []);

  useEffect(() => {
    if (selectedClassId) {
      loadMaterials(selectedClassId);
    } else {
      setMaterials([]);
    }
  }, [selectedClassId, loadMaterials]);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData(prev => ({ ...prev, file }));
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      type: 'PDF',
      url: '',
      file: null
    });
    setShowAddForm(false);
  };

  const handleAddMaterial = async () => {
    if (!selectedClassId) {
      alert('Please select a class first.');
      return;
    }
    if (!formData.title.trim()) {
      alert('Please enter a title.');
      return;
    }
    const isLinkType = formData.type === 'Link' || formData.type === 'Video';
    if (isLinkType && !formData.url.trim()) {
      alert('Please enter a URL for this material type.');
      return;
    }
    if (!isLinkType && !formData.file && !formData.url.trim()) {
      alert('Please upload a file or enter a URL.');
      return;
    }

    setSaving(true);
    try {
      let fileUrl = formData.url.trim();
      let fileName = '';

      if (!isLinkType && formData.file) {
        const file = formData.file;
        fileName = file.name;
        const storageRef = ref(storage, `learningMaterials/${selectedClassId}/${Date.now()}_${file.name}`);
        await uploadBytes(storageRef, file);
        fileUrl = await getDownloadURL(storageRef);
      }

      await addDoc(collection(db, 'classes', selectedClassId, 'learningMaterials'), {
        title: formData.title.trim(),
        description: formData.description.trim(),
        type: formData.type,
        fileUrl,
        fileName: fileName || formData.url.trim(),
        uploadedBy: teacherData.uid,
        uploadedAt: serverTimestamp(),
        schoolId: teacherData.schoolId || null
      });

      resetForm();
      await loadMaterials(selectedClassId);
    } catch (error) {
      console.error('Error adding material:', error);
      alert('Failed to add material. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteMaterial = async (material) => {
    if (!window.confirm('Are you sure you want to delete this material?')) return;
    try {
      // Delete from Firestore
      await deleteDoc(doc(db, 'classes', selectedClassId, 'learningMaterials', material.id));

      // Try to delete from Storage if it was a file upload
      if (material.fileName && material.fileUrl && material.type !== 'Link' && material.type !== 'Video') {
        try {
          const storageRef = ref(storage, material.fileUrl);
          await deleteObject(storageRef);
        } catch (storageError) {
          console.warn('Could not delete file from storage:', storageError);
        }
      }

      await loadMaterials(selectedClassId);
    } catch (error) {
      console.error('Error deleting material:', error);
      alert('Failed to delete material. Please try again.');
    }
  };

  const formatDate = (dateValue) => {
    if (!dateValue) return 'N/A';
    if (dateValue.toDate) return dateValue.toDate().toLocaleDateString();
    return new Date(dateValue).toLocaleDateString();
  };

  if (loading) {
    return (
      <div className="teacher-materials-view">
        <div className="loading">Loading...</div>
      </div>
    );
  }

  return (
    <div className="teacher-materials-view">
      <h2>Learning Materials</h2>

      {classes.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">📚</div>
          <h3>No Classes Assigned</h3>
          <p>You need a class assigned to manage learning materials.</p>
        </div>
      ) : (
        <>
          <div className="materials-controls">
            <div className="class-selector">
              <label>Select Class:</label>
              <select
                value={selectedClassId}
                onChange={(e) => setSelectedClassId(e.target.value)}
              >
                {classes.map(cls => (
                  <option key={cls.id} value={cls.id}>
                    {cls.name} {cls.grade ? `- ${cls.grade}` : ''}
                  </option>
                ))}
              </select>
            </div>
            <button
              className="add-material-button"
              onClick={() => setShowAddForm(true)}
              disabled={!selectedClassId}
            >
              + Add Material
            </button>
          </div>

          {loadingMaterials ? (
            <div className="loading">Loading materials...</div>
          ) : materials.length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon">📚</div>
              <h3>No Learning Materials</h3>
              <p>No materials have been added for this class yet. Click "Add Material" to get started.</p>
            </div>
          ) : (
            <div className="materials-grid">
              {materials.map(material => (
                <div key={material.id} className="material-card">
                  <div className="material-header">
                    <span className="material-type-icon">{getTypeIcon(material.type)}</span>
                    <span className="material-type-badge">{material.type}</span>
                  </div>
                  <h3 className="material-title">{material.title}</h3>
                  {material.description && (
                    <p className="material-description">{material.description}</p>
                  )}
                  <div className="material-meta">
                    <span>📅 {formatDate(material.uploadedAt)}</span>
                  </div>
                  <div className="material-actions">
                    {material.fileUrl && (
                      <a
                        href={material.fileUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="view-material-button"
                      >
                        {material.type === 'Link' || material.type === 'Video' ? 'Open Link' : 'View File'}
                      </a>
                    )}
                    <button
                      className="delete-material-button"
                      onClick={() => handleDeleteMaterial(material)}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {showAddForm && (
            <div className="modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) resetForm(); }}>
              <div className="modal-content">
                <div className="modal-header">
                  <h3>Add Learning Material</h3>
                  <button className="close-button" onClick={resetForm}>×</button>
                </div>
                <div className="modal-body">
                  <div className="form-group">
                    <label>Title *</label>
                    <input
                      type="text"
                      value={formData.title}
                      onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                      placeholder="Enter material title"
                    />
                  </div>
                  <div className="form-group">
                    <label>Description</label>
                    <textarea
                      value={formData.description}
                      onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                      placeholder="Enter a description (optional)"
                      rows={3}
                    />
                  </div>
                  <div className="form-group">
                    <label>Type *</label>
                    <select
                      value={formData.type}
                      onChange={(e) => setFormData(prev => ({ ...prev, type: e.target.value, url: '', file: null }))}
                    >
                      {MATERIAL_TYPES.map(type => (
                        <option key={type} value={type}>{type}</option>
                      ))}
                    </select>
                  </div>
                  {(formData.type === 'Link' || formData.type === 'Video') ? (
                    <div className="form-group">
                      <label>URL *</label>
                      <input
                        type="url"
                        value={formData.url}
                        onChange={(e) => setFormData(prev => ({ ...prev, url: e.target.value }))}
                        placeholder="https://example.com"
                      />
                    </div>
                  ) : (
                    <>
                      <div className="form-group">
                        <label>Upload File</label>
                        <input
                          type="file"
                          onChange={handleFileChange}
                          accept={formData.type === 'PDF' ? '.pdf' : formData.type === 'Image' ? 'image/*' : formData.type === 'Presentation' ? '.ppt,.pptx' : '*/*'}
                        />
                        {formData.file && (
                          <p className="file-name">Selected: {formData.file.name}</p>
                        )}
                      </div>
                      <div className="form-group">
                        <label>Or enter URL</label>
                        <input
                          type="url"
                          value={formData.url}
                          onChange={(e) => setFormData(prev => ({ ...prev, url: e.target.value }))}
                          placeholder="https://example.com/resource"
                        />
                      </div>
                    </>
                  )}
                </div>
                <div className="modal-footer">
                  <button className="cancel-button" onClick={resetForm}>Cancel</button>
                  <button
                    className="confirm-button"
                    onClick={handleAddMaterial}
                    disabled={saving}
                  >
                    {saving ? 'Saving...' : 'Save Material'}
                  </button>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default TeacherLearningMaterialsView;
